/**
 * SHSAT MathLive virtual-keyboard setup, extracted from the legacy
 * `Module/index.tsx` equation-editor effect so the adaptive flow can use the
 * same custom keyboard. Pure DOM/MathLive — no React.
 *
 * `setupShsatMathKeyboard(mf, questionType, onClear)` configures the global
 * `mathVirtualKeyboard` for a math-field: the SHSAT keyboard layout (type 11
 * = full, type 12 = numeric), physical-keyboard input restriction + paste
 * block, injected keyboard styling, a custom toolbar (copy/paste removed,
 * delete-all added), accessibility tooltips, and observers that keep the
 * keyboard visible. Returns a cleanup function that removes everything AND
 * hides the keyboard — important for the adaptive flow, which mounts/unmounts
 * the equation field per slot (legacy relied on the next question reconfiguring
 * it, so it never hid on its own).
 */

const LAYOUT_11 = {
  rows: [
    ['1', '2', '3', '4', '5', 'x', 'y', ''],
    ['6', '7', '8', '9', '0', '+', '−', '\\sqrt{#0}'],
    ['\\%', '−', '.', '\\frac{#0}{#0}', '{#0}\\frac{#0}{#0}', '\\cdot', '\\div', '\\sqrt[#0]{#0}'],
    ['', '{#0}^{#0}', '({#0})', '\\le', '\\lt', '=', '\\ge', '\\gt'],
    ['\\pi', '', '', '', '', '', '', '\\lvert{#@}\\rvert'],
  ],
};

const LAYOUT_12 = {
  rows: [
    ['1', '2', '3', '4', '5', '', '', ''],
    ['6', '7', '8', '9', '0', '', '', ''],
    ['\\%', '-', '.', '\\frac{#0}{#0}', '{#0}\\frac{#0}{#0}', '', '', ''],
  ],
};

const BASE_TOOLTIPS: Record<string, string> = {
  '1': 'one', '2': 'two', '3': 'three', '4': 'four', '5': 'five',
  '6': 'six', '7': 'seven', '8': 'eight', '9': 'nine', '0': 'zero',
  'x': 'letter x', 'y': 'letter y', '+': 'add', '÷': 'divide',
  '.': 'decimal point', '%': 'percent sign', '=': 'equal',
  '≤': 'less than or equal', '<': 'less than', '≥': 'greater than or equal',
  '>': 'greater than', 'π': 'constant pi', '⋅': 'multiply dot',
  '√▢​': 'square root', '▢√▢​': 'general root', '▢▢': 'exponent',
  '(▢)': 'parentheses', '∣■∣': 'absolute value', '▢▢​': 'fraction',
  '▢▢▢​': 'mixed number',
};

const KEYBOARD_STYLE = `
  math-field::part(menu-toggle),
  math-field::part(virtual-keyboard-toggle) { display: none !important; }
  .digitalSHSATModule__eeContainer { position: relative; }
  .digitalSHSATModule__eeContainer .ML__virtual-keyboard {
    position: absolute !important;
    top: calc(100% + 8px) !important;
    left: 0 !important;
    width: 620px !important;
    z-index: 999 !important;
  }
  .digitalSHSATModule__eeContainer .MLK__backdrop { background: none; }
  .digitalSHSATModule__eeContainer .MLK__keycap {
    border: 1px solid #676767; flex: 1; cursor: pointer;
  }
  .digitalSHSATModule__eeContainer .MLK__keycap:hover {
    outline: 2px solid #676767 !important;
    box-shadow: 0 0 4px rgba(233, 233, 233, 0.3);
  }
  .digitalSHSATModule__eeContainer .MLK__keycap,
  .digitalSHSATModule__eeContainer .MLK__keycap::after { transition: none !important; }
  .digitalSHSATModule__eeContainer .MLK__keycap:hover::after {
    content: attr(data-tooltip);
    position: absolute; top: -28px; transform: translateX(-30%);
    background: rgba(0,0,0,0.8); color: white; padding: 2px 6px;
    border-radius: 4px; font-size: 12px; white-space: nowrap;
    pointer-events: none; z-index: 1000;
  }
  .digitalSHSATModule__eeContainer .MLK__keycap:active { background: none !important; }
  .ML__latex .ML__vlist-t2, .ML__delim-size1 { font-size: 16px; }
  .digitalSHSATModule__eeContainer .MLK__toolbar { justify-content: flex-start; display: none; }
  .digitalSHSATModule__eeContainer .MLK__plate { border: 1px solid #bdbdbd; padding: 4px; }
  .digitalSHSATModule__eeContainer :where(.MLK__rows > .MLK__row div) {
    border: 1px solid #bdbdbd; border-radius: 1px; font-size: 18px; background: white;
  }
  .digitalSHSATModule__eeContainer .MLK__rows > .MLK__row div:empty {
    pointer-events: none; opacity: 0.5; background-color: white; border: 1px solid #e0e0e0ff;
  }
  .digitalSHSATModule__eeContainer .MLK__rows { gap: 5px; }
  .digitalSHSATModule__eeContainer .MLK__rows > .MLK__row { gap: 5px; justify-content: flex-start; }
  .digitalSHSATModule__eeContainer .MLK__row:nth-child(-n+2) div:nth-child(-n+5) {
    background-color: #eeeeeec7 !important;
  }
  /* High-contrast labels. MathLive's virtual keyboard defaults to a dark theme
     (light glyphs); this keypad recolored the keys to white but left the default
     glyph color, so the digits and the toolbar undo/redo icons rendered nearly
     invisible (the trash icon is a custom button with its own dark color, which
     is why only it showed). Reset the glyph color via MathLive's own CSS
     variables, with explicit fallbacks on the keycaps and toolbar icons. */
  /* IMPORTANT: scope these to .ML__keyboard, NOT .digitalSHSATModule__eeContainer.
     MathLive's virtual keyboard is a global singleton that renders at the
     document level (its root is .ML__keyboard), so it is NOT a descendant of
     our eeContainer — eeContainer-scoped rules never match the keycaps.
     MathLive computes --_keycap-text: var(--keycap-text, #e3e4e8), falling back
     to a near-white dark-theme default; setting the public variables on the
     keyboard root makes the labels/toolbar icons render dark. */
  .ML__keyboard {
    --keycap-text: #1a1a1a;
    --keycap-text-active: #1a1a1a;
    --keycap-text-hover: #1a1a1a;
    --keycap-text-pressed: #1a1a1a;
    --keycap-secondary-text: #1a1a1a;
    --keyboard-toolbar-text: #1a1a1a;
    --keyboard-toolbar-text-active: #1a1a1a;
  }
  /* Explicit fallback: the visible glyph is a .ML__cmr span inside .ML__latex
     inside .MLK__keycap, whose color is inherited — force it dark directly. */
  .ML__keyboard .MLK__keycap:not(:empty),
  .ML__keyboard .MLK__keycap:not(:empty) *,
  .ML__keyboard .ML__latex,
  .ML__keyboard .ML__latex * {
    color: #1a1a1a !important;
  }
  .ML__keyboard .MLK__keycap:not(:empty) { opacity: 1 !important; }
  /* Toolbar icons (undo/redo). Color only — leave MathLive's disabled-state
     dimming intact so a button with nothing to undo still reads as disabled. */
  .ML__keyboard .MLK__toolbar .action svg {
    fill: currentColor !important;
  }
`;

export function setupShsatMathKeyboard(
  mf: any,
  questionType: number,
  onClear: () => void,
): () => void {
  const keyboard = (window as any).mathVirtualKeyboard;
  if (!keyboard || !mf) return () => {};

  mf.mathVirtualKeyboardPolicy = 'manual';
  mf.setAttribute('virtual-keyboard-toolbar', 'none');

  // Physical-keyboard input restriction: digits + a fixed set of operators
  // only; block paste.
  const handleKeydown = (e: KeyboardEvent) => {
    const navKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab'];
    if (navKeys.includes(e.key)) return;
    const allowed = /^[0-9]$/.test(e.key) || ['<', '>', '/', '.', '+', '-', '=', '%', '*', '|'].includes(e.key);
    if (!allowed) { e.preventDefault(); e.stopPropagation(); }
  };
  const handlePaste = (e: ClipboardEvent) => { e.preventDefault(); e.stopPropagation(); };
  mf.addEventListener('keydown', handleKeydown);
  mf.addEventListener('paste', handlePaste);

  keyboard.layouts = questionType === 12 ? LAYOUT_12 : LAYOUT_11;
  keyboard.show();
  keyboard.visible = true;

  // Accessibility tooltips on keycaps. The minus key appears twice in the
  // type-11 layout: first is "subtract", subsequent are "negative sign".
  const tooltipTimer = window.setTimeout(() => {
    const keys = document.querySelectorAll('.digitalSHSATModule__eeContainer .MLK__keycap');
    let minusCount = 0;
    keys.forEach((key) => {
      const k = key.textContent || '';
      let tooltip = BASE_TOOLTIPS[k] ?? '';
      if (k === '−' || k === '-') {
        minusCount++;
        tooltip = minusCount === 1 ? 'subtract' : 'negative sign';
      }
      (key as HTMLElement).setAttribute('data-tooltip', tooltip);
    });
  }, 100);

  const style = document.createElement('style');
  style.innerHTML = KEYBOARD_STYLE;
  document.head.appendChild(style);

  const container = mf.closest('.digitalSHSATModule__eeContainer')
    || document.querySelector('.digitalSHSATModule__eeContainer');
  let visibilityObserver: MutationObserver | null = null;
  let toolbarObserver: MutationObserver | null = null;

  if (container) {
    keyboard.container = container;
    keyboard.alwaysVisible = true;
    keyboard.floater = false;
    keyboard.autoDismiss = false;
    keyboard.show();
  }

  const domTimer = window.setTimeout(() => {
    const vk = container?.querySelector('.ML__keyboard') as HTMLElement | null;
    if (vk) {
      vk.style.pointerEvents = 'auto';
      vk.style.opacity = '1';
      vk.style.visibility = 'visible';
      vk.style.removeProperty('--_keyboard-height');
      vk.style.height = '300px';

      const setupToolbar = () => {
        const toolbar = vk.querySelector('.MLK__toolbar') as HTMLElement | null;
        if (!toolbar) return;
        toolbar.style.display = 'flex';
        // Remove copy/paste buttons.
        toolbar.querySelectorAll('[data-command]').forEach((btn) => {
          const cmd = btn.getAttribute('data-command') || '';
          if (cmd.includes('pasteFromClipboard') || cmd.includes('copyToClipboard')) btn.remove();
        });
        // Add a delete-all button.
        if (!toolbar.querySelector('[data-command="deleteAll"]')) {
          const del = document.createElement('div');
          del.className = 'action';
          del.setAttribute('data-command', 'deleteAll');
          del.setAttribute('data-tooltip', 'Clear all');
          del.innerHTML = '<svg viewBox="0 0 20 20" width="18" height="20"><path fill="currentColor" d="M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12ZM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4Z"/></svg>';
          del.addEventListener('click', () => {
            if (mf && typeof mf.setValue === 'function') mf.setValue('');
            onClear();
          });
          Object.assign(del.style, {
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '4px', cursor: 'pointer', color: '#333', marginRight: '8px',
            height: '32px', minWidth: '32px', boxSizing: 'border-box',
          });
          toolbar.appendChild(del);
        }
      };
      setupToolbar();
      toolbarObserver = new MutationObserver(setupToolbar);
      toolbarObserver.observe(vk, { childList: true, subtree: true });

      // Keep the keyboard visible if MathLive tries to hide it.
      visibilityObserver = new MutationObserver(() => {
        if (vk.style.display === 'none' || vk.style.visibility === 'hidden' || vk.style.opacity === '0') {
          vk.style.display = 'block';
          vk.style.visibility = 'visible';
          vk.style.opacity = '1';
          vk.style.pointerEvents = 'auto';
        }
      });
      visibilityObserver.observe(vk, { attributes: true, attributeFilter: ['style'], subtree: false });
    }
    if (mf) {
      mf.focus();
      mf.addEventListener('focusin', onFocusIn);
      mf.addEventListener('focusout', onFocusOut);
    }
  }, 50);

  const onFocusIn = () => { mf.style.border = '2px solid #2f80ed'; };
  const onFocusOut = () => { mf.style.border = '1px solid #bdbdbd'; };

  return () => {
    window.clearTimeout(tooltipTimer);
    window.clearTimeout(domTimer);
    if (style.parentNode) style.parentNode.removeChild(style);
    if (visibilityObserver) visibilityObserver.disconnect();
    if (toolbarObserver) toolbarObserver.disconnect();
    mf.removeEventListener('keydown', handleKeydown);
    mf.removeEventListener('paste', handlePaste);
    mf.removeEventListener('focusin', onFocusIn);
    mf.removeEventListener('focusout', onFocusOut);
    // Hide the global keyboard so it doesn't leak onto non-equation slots
    // (adaptive unmounts the field per slot; legacy left this to the next
    // question's reconfigure).
    try {
      keyboard.hide();
      keyboard.visible = false;
      mf.mathVirtualKeyboardPolicy = 'auto';
    } catch (_e) { /* keyboard may already be torn down */ }
  };
}
